import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { saveAs } from "file-saver";
import { CalendarDays, Eye, FileText, Hash, LayoutDashboard, RefreshCw, Save } from "lucide-react";
import { calculateTotals, generateInvoicePdfBytes } from "../lib/invoicePdfGenerator.mjs";

const selectAllOnFocus = (e) => e.target.select();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InvoiceGenerator() {
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingInvoiceNumber, setLoadingInvoiceNumber] = useState(true);
  const [saving, setSaving] = useState(false);
  const [nextInvoiceNumberInput, setNextInvoiceNumberInput] = useState("");
  const [savingNextInvoiceNumber, setSavingNextInvoiceNumber] = useState(false);

  const [invoice, setInvoice] = useState({
    invoiceNumber: "CITS-00001",
    date: new Date().toISOString().slice(0, 10),
    dueDate: "",
    currency: "AUD",
    taxPercent: 10,
    from: {
      name: "CONNECTIFY IT SERVICES",
      address: "Multiple cities across Australia",
      email: "info@connect-ify.com.au",
    },
    to: {
      name: "Client Name",
      address: "Client Address Line 1\nCity, Country",
      email: "",
    },
    items: [{ description: "Consulting services", qty: 1, rate: 100, discount: 0 }],
    notes: "",
  });

  useEffect(() => {
    const token = localStorage.getItem("admin_token");

    if (!token) {
      navigate("/admin");
      return;
    }

    fetch(`${API}/admin/verify`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => {
        if (response.ok) {
          setIsAuthorized(true);
        } else {
          localStorage.removeItem("admin_token");
          navigate("/admin");
        }
      })
      .catch(() => {
        navigate("/admin");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [navigate]);

  const loadNextInvoiceNumber = async () => {
    const token = localStorage.getItem("admin_token");

    try {
      const response = await fetch(`${API}/admin/next-invoice-number`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error("Failed to get invoice number");
      const data = await response.json();
      return data.invoiceNumber;
    } catch (error) {
      console.error("Error loading invoice number:", error);
      return null;
    }
  };

  useEffect(() => {
    if (!isAuthorized) return;

    loadNextInvoiceNumber().then((invoiceNumber) => {
      if (invoiceNumber) {
        setInvoice((prev) => ({ ...prev, invoiceNumber }));
        setNextInvoiceNumberInput(invoiceNumber);
      }
      setLoadingInvoiceNumber(false);
    });
  }, [isAuthorized]);

  const resetToAutoNumber = async () => {
    const autoNumber = await loadNextInvoiceNumber();
    if (autoNumber) {
      setInvoice((prev) => ({ ...prev, invoiceNumber: autoNumber }));
      setNextInvoiceNumberInput(autoNumber);
      toast.success(`Reset to ${autoNumber}`);
    }
  };

  const setNextInvoiceNumber = async () => {
    const token = localStorage.getItem("admin_token");
    const value = nextInvoiceNumberInput.trim();

    if (!value) {
      toast.error("Enter the next invoice number first");
      return;
    }

    setSavingNextInvoiceNumber(true);
    try {
      const response = await fetch(`${API}/admin/next-invoice-number`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ nextInvoiceNumber: value }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || "Failed to update next invoice number");
      }

      setInvoice((prev) => ({ ...prev, invoiceNumber: data.invoiceNumber }));
      setNextInvoiceNumberInput(data.invoiceNumber);
      toast.success(data.message || `Next invoice number set to ${data.invoiceNumber}`);
    } catch (error) {
      console.error("Error updating next invoice number:", error);
      toast.error(error.message || "Failed to update next invoice number");
    } finally {
      setSavingNextInvoiceNumber(false);
    }
  };

  const updateField = (path, value) => {
    setInvoice((cur) => {
      const copy = structuredClone(cur);
      const keys = path.split(".");
      let obj = copy;
      keys.slice(0, -1).forEach((k) => (obj = obj[k]));
      obj[keys.at(-1)] = value;
      return copy;
    });
  };

  const updateItem = (i, key, value) => {
    setInvoice((cur) => ({
      ...cur,
      items: cur.items.map((it, idx) => (idx === i ? { ...it, [key]: value } : it)),
    }));
  };

  const addItem = () =>
    setInvoice((cur) => ({
      ...cur,
      items: [...cur.items, { description: "", qty: 1, rate: 0, discount: 0 }],
    }));

  const removeItem = (i) =>
    setInvoice((cur) => ({
      ...cur,
      items: cur.items.filter((_, idx) => idx !== i),
    }));

  const saveInvoiceToBackend = async () => {
    const token = localStorage.getItem("admin_token");
    const { subtotal, tax, total } = calculateTotals(invoice.items, invoice.taxPercent);

    const invoiceData = {
      invoiceNumber: invoice.invoiceNumber,
      date: invoice.date,
      dueDate: invoice.dueDate,
      currency: invoice.currency,
      taxPercent: invoice.taxPercent,
      from: invoice.from,
      to: invoice.to,
      items: invoice.items,
      notes: invoice.notes,
      subtotal,
      tax,
      total,
    };

    try {
      const response = await fetch(`${API}/admin/invoices`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(invoiceData),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Invoice ${data.invoiceNumber} saved successfully!`);
        return true;
      }

      const error = await response.json();
      toast.error(error.detail || "Failed to save invoice");
      return false;
    } catch (error) {
      console.error("Error saving invoice:", error);
      toast.error("Error saving invoice");
      return false;
    }
  };

  const generatePdf = async () => {
    const response = await fetch("/Letterpad.pdf");
    const templateBytes = await response.arrayBuffer();
    const pdfBytes = await generateInvoicePdfBytes(invoice, templateBytes);
    saveAs(new Blob([pdfBytes]), `${invoice.invoiceNumber}.pdf`);
  };

  const handleGenerateAndSave = async () => {
    setSaving(true);
    const saved = await saveInvoiceToBackend();
    if (saved) {
      await generatePdf();
    }
    setSaving(false);
  };

  if (loading || loadingInvoiceNumber) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[linear-gradient(180deg,#f7fbff_0%,#eef6ff_22%,#ffffff_50%,#f6fbff_100%)] dark:bg-[linear-gradient(180deg,#08111f_0%,#0b1729_35%,#0a1220_100%)]">
        <div className="text-gray-600 dark:text-gray-400">
          {loading ? "Verifying access..." : "Loading invoice number..."}
        </div>
      </div>
    );
  }

  if (!isAuthorized) return null;

  const { subtotal, tax, total } = calculateTotals(invoice.items, invoice.taxPercent);

  const shell =
    "rounded-[1.75rem] border border-slate-200 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(241,245,249,0.95))] p-6 shadow-[0_24px_65px_-28px_rgba(14,165,233,0.18)] dark:border-white/10 dark:bg-[linear-gradient(180deg,rgba(15,23,42,0.88),rgba(15,23,42,0.58))]";
  const field =
    "w-full p-2 border rounded-xl bg-white dark:bg-white text-slate-900 border-slate-300 dark:border-white/10 focus:ring-2 focus:ring-blue-500";

  return (
    <div className="min-h-screen overflow-hidden bg-[linear-gradient(180deg,#f7fbff_0%,#eef6ff_22%,#ffffff_50%,#f6fbff_100%)] pt-16 text-slate-900 dark:bg-[linear-gradient(180deg,#08111f_0%,#0b1729_35%,#0a1220_100%)] dark:text-white">
      <section className="relative isolate overflow-hidden border-b border-white/50 dark:border-white/10">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-[-8rem] top-24 h-72 w-72 rounded-full bg-cyan-300/30 blur-3xl dark:bg-cyan-400/10" />
          <div className="absolute right-[-6rem] top-12 h-80 w-80 rounded-full bg-blue-500/20 blur-3xl dark:bg-blue-500/20" />
          <div className="absolute bottom-[-5rem] left-1/3 h-64 w-64 rounded-full bg-emerald-300/20 blur-3xl dark:bg-emerald-400/10" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <div className="mb-6 inline-flex items-center rounded-full border border-sky-200 bg-white/80 px-4 py-2 text-sm font-medium text-sky-700 shadow-sm backdrop-blur dark:border-sky-400/20 dark:bg-white/5 dark:text-sky-200">
                Invoice generator and numbering controls
              </div>
              <h2 className="text-4xl font-black tracking-tight text-slate-950 sm:text-5xl dark:text-white">
                Create invoices with
                <span className="block bg-gradient-to-r from-sky-500 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
                  cleaner control and live preview.
                </span>
              </h2>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-600 dark:text-slate-300">
                Set the next invoice number, edit invoice details, and generate the final PDF from one place.
              </p>
            </div>

            <div className="flex flex-col gap-4">
              <button
                type="button"
                onClick={() => navigate("/admin/dashboard")}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 font-semibold text-white transition duration-300 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-100"
              >
                <LayoutDashboard className="h-4 w-4" />
                Dashboard
              </button>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {[
                { icon: FileText, label: "Current number", value: invoice.invoiceNumber },
                { icon: CalendarDays, label: "Invoice date", value: invoice.date || "Not set" },
                { icon: Eye, label: "Mode", value: "Live preview" },
              ].map((item) => (
                <div key={item.label} className="rounded-2xl border border-white/70 bg-white/85 p-5 shadow-[0_20px_50px_-30px_rgba(15,23,42,0.4)] backdrop-blur dark:border-white/10 dark:bg-white/5">
                  <div className="flex items-center gap-3">
                    <div className="rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-400 p-3 text-white">
                      <item.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="text-sm text-slate-600 dark:text-slate-300">{item.label}</div>
                      <div className="font-semibold text-slate-950 dark:text-white">{item.value}</div>
                    </div>
                  </div>
                </div>
              ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-6">
            <div className={shell}>
              <h3 className="text-lg font-semibold mb-4 text-slate-950 dark:text-white">Invoice Details</h3>

              <div className="space-y-4">
                <div className="rounded-2xl border border-slate-200 bg-white/80 p-4 dark:border-white/10 dark:bg-white/5">
                  <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                    Set Next Invoice Number
                  </label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Hash className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <input
                        value={nextInvoiceNumberInput}
                        onChange={(e) => setNextInvoiceNumberInput(e.target.value)}
                        placeholder="CITS-0042"
                        className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-10 pr-3 text-slate-900 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-200 dark:border-white/10 dark:bg-white dark:text-slate-900"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={setNextInvoiceNumber}
                      disabled={savingNextInvoiceNumber}
                      className="px-4 py-2 rounded-xl bg-[#1E90FF] hover:bg-[#1570d6] text-white transition-colors"
                    >
                      {savingNextInvoiceNumber ? "Saving..." : "Set"}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                    Enter `42` or `CITS-0042` to control the next generated invoice number.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Invoice Number</label>
                  <div className="flex gap-2">
                    <input
                      value={invoice.invoiceNumber}
                      onFocus={selectAllOnFocus}
                      onChange={(e) => updateField("invoiceNumber", e.target.value)}
                      className={field}
                    />
                    <button
                      type="button"
                      onClick={resetToAutoNumber}
                      className="px-3 py-2 bg-slate-500 hover:bg-slate-600 text-white rounded-xl transition-colors text-sm"
                      title="Reset to auto-generated number"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                    Editable. Use refresh to reset to the current next number.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Date</label>
                    <input type="date" value={invoice.date} onFocus={selectAllOnFocus} onChange={(e) => updateField("date", e.target.value)} className={field} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Due Date</label>
                    <input type="date" value={invoice.dueDate} onFocus={selectAllOnFocus} onChange={(e) => updateField("dueDate", e.target.value)} className={field} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Currency</label>
                    <input value={invoice.currency} onFocus={selectAllOnFocus} onChange={(e) => updateField("currency", e.target.value)} className={field} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Tax (%)</label>
                    <input type="number" value={invoice.taxPercent} onFocus={selectAllOnFocus} onChange={(e) => updateField("taxPercent", Number(e.target.value))} className={field} />
                  </div>
                </div>
              </div>
            </div>

            <div className={shell}>
              <h3 className="text-lg font-semibold mb-4 text-slate-950 dark:text-white">From (Your Company)</h3>
              <div className="space-y-3">
                <input placeholder="Company name" value={invoice.from.name} onFocus={selectAllOnFocus} onChange={(e) => updateField("from.name", e.target.value)} className={field} />
                <textarea placeholder="Company address" value={invoice.from.address} onFocus={selectAllOnFocus} onChange={(e) => updateField("from.address", e.target.value)} rows={3} className={field} />
                <input placeholder="Company email" value={invoice.from.email} onFocus={selectAllOnFocus} onChange={(e) => updateField("from.email", e.target.value)} className={field} />
              </div>
            </div>

            <div className={shell}>
              <h3 className="text-lg font-semibold mb-4 text-slate-950 dark:text-white">Bill To (Client)</h3>
              <div className="space-y-3">
                <input placeholder="Client name" value={invoice.to.name} onFocus={selectAllOnFocus} onChange={(e) => updateField("to.name", e.target.value)} className={field} />
                <textarea placeholder="Client address" value={invoice.to.address} onFocus={selectAllOnFocus} onChange={(e) => updateField("to.address", e.target.value)} rows={3} className={field} />
                <input placeholder="Client email" value={invoice.to.email} onFocus={selectAllOnFocus} onChange={(e) => updateField("to.email", e.target.value)} className={field} />
              </div>
            </div>

            <div className={shell}>
              <h3 className="text-lg font-semibold mb-4 text-slate-950 dark:text-white">Items</h3>
              <div className="space-y-3">
                <div className="grid grid-cols-12 gap-2 text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  <div className="col-span-5">Description</div>
                  <div className="col-span-2 text-right">Qty</div>
                  <div className="col-span-3 text-right">Rate</div>
                  <div className="col-span-2 text-right">Action</div>
                </div>

                {invoice.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <input className={`col-span-5 ${field}`} placeholder="Description" value={it.description} onFocus={selectAllOnFocus} onChange={(e) => updateItem(i, "description", e.target.value)} />
                    <input type="number" className={`col-span-2 ${field}`} placeholder="Qty" value={it.qty} onFocus={selectAllOnFocus} onChange={(e) => updateItem(i, "qty", Number(e.target.value))} />
                    <input type="number" className={`col-span-3 ${field}`} placeholder="Rate" value={it.rate} onFocus={selectAllOnFocus} onChange={(e) => updateItem(i, "rate", Number(e.target.value))} />
                    <button type="button" onClick={() => removeItem(i)} className="col-span-2 px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-xl transition-colors">
                      Remove
                    </button>
                  </div>
                ))}

                <button type="button" onClick={addItem} className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors">
                  + Add Item
                </button>
              </div>
            </div>

            <div className={shell}>
              <h3 className="text-lg font-semibold mb-4 text-slate-950 dark:text-white">Notes</h3>
              <textarea placeholder="Additional message (optional)" value={invoice.notes} onFocus={selectAllOnFocus} onChange={(e) => updateField("notes", e.target.value)} rows={3} className={field} />
            </div>

            <button
              type="button"
              onClick={handleGenerateAndSave}
              disabled={saving}
              className="w-full px-6 py-4 bg-green-600 hover:bg-green-700 text-white rounded-2xl font-semibold transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            >
              {saving ? "Saving..." : <><Save className="h-5 w-5" />Save & Download PDF Invoice</>}
            </button>
          </div>

          <div className="lg:sticky lg:top-24 h-fit">
            <div className={shell}>
              <h3 className="text-lg font-semibold mb-4 text-slate-950 dark:text-white">Preview</h3>
              <div className="border border-slate-300 dark:border-white/10 rounded-2xl bg-white p-8 overflow-auto" style={{ minHeight: 600 }}>
                <div className="max-w-2xl mx-auto">
                  <div className="flex justify-between mb-8">
                    <div>
                      <div className="font-bold text-lg text-slate-900">{invoice.from.name}</div>
                      <div className="text-sm text-slate-600 whitespace-pre-line">{invoice.from.address}</div>
                      <div className="text-sm text-slate-600">{invoice.from.email}</div>
                    </div>

                    <div className="text-right">
                      <div className="font-semibold text-slate-900">Invoice: {invoice.invoiceNumber}</div>
                      <div className="text-sm text-slate-600">Date: {invoice.date}</div>
                      <div className="text-sm text-slate-600">Due: {invoice.dueDate || "N/A"}</div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <strong className="text-slate-900">Bill To:</strong>
                    <div className="text-slate-700">{invoice.to.name}</div>
                    <div className="text-sm text-slate-600 whitespace-pre-line">{invoice.to.address}</div>
                    {invoice.to.email && <div className="text-sm text-slate-600">{invoice.to.email}</div>}
                  </div>

                  <table className="w-full border-collapse mb-6">
                    <thead>
                      <tr className="border-b-2 border-slate-300">
                        <th className="text-left py-2 text-slate-900">Description</th>
                        <th className="text-right py-2 text-slate-900 w-16">Qty</th>
                        <th className="text-right py-2 text-slate-900 w-24">Rate</th>
                        <th className="text-right py-2 text-slate-900 w-24">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoice.items.map((it, idx) => {
                        const amount =
                          (Number(it.qty) || 0) * (Number(it.rate) || 0) - (Number(it.discount) || 0);
                        return (
                          <tr key={idx} className="border-b border-slate-200">
                            <td className="py-2 text-slate-700">{it.description}</td>
                            <td className="text-right py-2 text-slate-700">{it.qty}</td>
                            <td className="text-right py-2 text-slate-700">{Number(it.rate).toFixed(2)}</td>
                            <td className="text-right py-2 text-slate-700">{amount.toFixed(2)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  <div className="text-right space-y-1 mb-6">
                    <div className="text-slate-700">Subtotal: {subtotal.toFixed(2)} {invoice.currency}</div>
                    <div className="text-slate-700">Tax ({invoice.taxPercent}%): {tax.toFixed(2)} {invoice.currency}</div>
                    <div className="font-bold text-lg text-slate-900">
                      Total: {total.toFixed(2)} {invoice.currency}
                    </div>
                  </div>

                  {invoice.notes && (
                    <div className="mt-6 text-sm">
                      <strong className="text-slate-900">Notes:</strong>
                      <div className="text-slate-600">{invoice.notes}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
